(() => {
  'use strict';

  const TAG = 'woobles-mcd-redemption';
  const DEBUG = true;

  if (customElements.get(TAG)) {
    if (DEBUG) console.warn('[McD] Component already registered.');
    return;
  }

  function setVisible(element, visible) {
    if (!element) return;

    element.hidden = !visible;
    element.classList.toggle('hidden', !visible);
  }

  function startRedemptionTrace({ code, prefix, giftVariantId }) {
    if (!DEBUG) {
      return {
        result() {},
        error() {},
        stop() {},
      };
    }

    const label = `[McD ${Date.now().toString(36)}]`;
    const originalFetch = window.fetch;
    let requestNumber = 0;

    const normalize = (value) =>
      String(value || '')
        .trim()
        .toUpperCase();

    const log = (message, data) => {
      console.log(`${label} ${message}`, JSON.stringify(data, null, 2));
    };

    function summarize(data) {
      const items = Array.isArray(data?.items) ? data.items : data?.variant_id ? [data] : [];

      const discounts = Array.isArray(data?.discount_codes) ? data.discount_codes : null;

      return {
        currency: data?.currency,
        itemCount: data?.item_count,
        subtotalCents: data?.items_subtotal_price,

        discounts:
          discounts?.map((entry) => ({
            matchesSubmittedCode: normalize(entry.code) === code,
            applicable: entry.applicable,
          })) ?? null,

        submittedCodePresent: discounts ? discounts.some((entry) => normalize(entry.code) === code) : null,

        submittedCodeApplicable: discounts
          ? (discounts.find((entry) => normalize(entry.code) === code)?.applicable ?? null)
          : null,

        items: items.map((item) => {
          const properties = item.properties || {};

          return {
            variantId: String(item.variant_id),
            isExpectedGift: String(item.variant_id) === giftVariantId,
            quantity: item.quantity,
            originalLinePrice: item.original_line_price,
            finalLinePrice: item.final_line_price,
            checkoutGift: properties['_gwp-checkout-ui-gift'],
            mcdGift: properties['_mcd_code_gated_gift'],
            giftPrefix: properties['_mcd_discount_prefix'],
            giftCodeMatches: normalize(properties['_mcd_discount_code']) === code,
          };
        }),

        status: data?.status,
        message: data?.message,
        description: data?.description,
        errors: data?.errors,
      };
    }

    async function tracedFetch(...args) {
      const [input, options] = args;
      let url;

      try {
        url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url, location.href);
      } catch {
        return originalFetch.apply(this, args);
      }

      const isCartRequest =
        url.origin === location.origin && /\/cart(?:\/(?:add|update|change|clear))?\.js$/.test(url.pathname);

      if (!isCartRequest) {
        return originalFetch.apply(this, args);
      }

      const number = ++requestNumber;
      const method = options?.method || input?.method || 'GET';
      const name = `#${number} ${method} ${url.pathname}`;

      log(`${name} START`, {});

      let response;

      try {
        response = await originalFetch.apply(this, args);
      } catch (error) {
        log(`${name} NETWORK ERROR`, {
          name: error.name,
          message: error.message,
        });
        throw error;
      }

      // Inspect a clone without consuming the manager's response.
      // This does not create any additional cart requests.
      try {
        response
          .clone()
          .json()
          .then(
            (data) =>
              log(`${name} RESPONSE`, {
                httpStatus: response.status,
                httpOk: response.ok,
                ...summarize(data),
              }),
            (error) =>
              log(`${name} UNREADABLE RESPONSE`, {
                httpStatus: response.status,
                message: error.message,
              }),
          );
      } catch (error) {
        log(`${name} TRACE ERROR`, {
          message: error.message,
        });
      }

      return response;
    }

    window.fetch = tracedFetch;

    log('SUBMIT', {
      prefix,
      codeLength: code.length,
      giftVariantId,
      view: new URLSearchParams(location.search).get('view'),
      shopifyCountry: window.Shopify?.country,
      shopifyCurrency: window.Shopify?.currency?.active,
      managerReady: typeof window.WooblesCartGWP?.redeemMcdPromo === 'function',
    });

    return {
      result(result) {
        log('MANAGER RESULT — may already be after cleanup', {
          ok: result?.ok,
          reason: result?.reason,
          cart: result?.cart ? summarize(result.cart) : null,
        });
      },

      error(error) {
        log('REDEMPTION EXCEPTION', {
          name: error?.name,
          message: error?.message,
          stack: error?.stack,
        });
      },

      stop() {
        if (window.fetch === tracedFetch) {
          window.fetch = originalFetch;
        }

        log('END — pending response logs may follow', {});
      },
    };
  }

  class USPrizesRedemptionField extends HTMLElement {
    constructor() {
      super();

      this.processing = false;
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      this.init();

      if (!this.form) {
        queueMicrotask(() => {
          if (this.isConnected) this.init();
        });
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
    }

    init() {
      this.form?.removeEventListener('submit', this.handleSubmit);

      this.form = this.querySelector('form');
      this.inputGroup = this.querySelector('[data-input-group]');
      this.input = this.querySelector('input[name="discount"]');
      this.button = this.querySelector('[data-submit-button]');
      this.buttonText = this.querySelector('[data-submit-button-text]');
      this.buttonLoader = this.querySelector('[data-submit-button-loader]');
      this.successMessage = this.querySelector('[data-success-message]');
      this.errorMessage = this.querySelector('[data-error-message]');
      this.errorMessageText = this.querySelector('[data-error-message-text]');

      if (!this.form || !this.input || !this.button) {
        if (DEBUG) {
          console.warn('[McD] Form initialization incomplete', {
            componentId: this.id,
            formFound: Boolean(this.form),
            inputFound: Boolean(this.input),
            buttonFound: Boolean(this.button),
          });
        }
        return;
      }

      if (!this.initialized) {
        this.clearMessages();
        this.initialized = true;
      }

      this.setLoading(this.processing);
      this.form.addEventListener('submit', this.handleSubmit);

      if (DEBUG) {
        console.log('[McD] Submit listener attached', {
          componentId: this.id,
          prefix: this.dataset.discountCodePrefix,
          giftVariantId: this.dataset.giftVariantId,
        });
      }
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (this.processing) {
        if (DEBUG) console.warn('[McD] Duplicate submit ignored.');
        return;
      }

      this.clearMessages();

      const code = this.input.value.trim().toUpperCase();
      const prefix = String(this.dataset.discountCodePrefix || '')
        .trim()
        .toUpperCase();
      const giftVariantId = String(this.dataset.giftVariantId || '').trim();

      if (!prefix || !/^\d+$/.test(giftVariantId)) {
        if (DEBUG) {
          console.error('[McD] Invalid form settings', {
            prefix,
            giftVariantId,
          });
        }

        this.showError('This redemption form is not configured correctly. Please contact us.');
        return;
      }

      if (!code.startsWith(prefix) || code.length <= prefix.length) {
        if (DEBUG) {
          console.warn('[McD] Local code-format validation failed', {
            expectedPrefix: prefix,
            codeLength: code.length,
            prefixMatches: code.startsWith(prefix),
          });
        }

        this.showError(`Please enter your complete code beginning with ${prefix}.`, true);
        return;
      }

      this.processing = true;
      this.setLoading(true);

      let manager;
      let succeeded = false;

      const trace = startRedemptionTrace({
        code,
        prefix,
        giftVariantId,
      });

      try {
        manager = await this.getGwpManager();

        if (DEBUG) {
          console.log('[McD] Calling manager.redeemMcdPromo()');
        }

        const result = await manager.redeemMcdPromo({
          code,
          prefix,
          giftVariantId,
        });

        trace.result(result);

        if (result?.ok === true) {
          succeeded = true;
          setVisible(this.successMessage, true);
          this.input.value = '';
        } else {
          const messages = {
            busy: 'Your cart is updating. Please wait a moment and try again.',
            invalid_gift_config: 'This redemption form is not configured correctly. Please contact us.',
            invalid_code: 'This code could not be applied. Check your code and the promotion requirements.',
            not_applicable: 'This code could not be applied to your cart. Check the promotion requirements.',
          };

          this.showError(
            messages[result?.reason] ||
              this.dataset.errorMessage ||
              'We could not confirm your redemption. Please check your cart.',
            result?.reason === 'invalid_code' || result?.reason === 'not_applicable',
          );
        }
      } catch (error) {
        trace.error(error);

        // A failed response does not prove the cart was unchanged.
        // Let the manager reconcile instead of issuing competing writes.
        if (manager) {
          try {
            manager.requestReconcile?.('mcd_redemption_ui_error');

            if (DEBUG) {
              console.log('[McD] Requested reconciliation after exception.');
            }
          } catch (reconcileError) {
            if (DEBUG) {
              console.error('[McD] Could not request reconciliation', reconcileError);
            }
          }
        }

        this.showError(
          error?.code === 'manager_unavailable'
            ? 'Redemption is unavailable right now. Please refresh and try again.'
            : 'We could not confirm your redemption. Please check your cart before trying again.',
        );
      } finally {
        trace.stop();

        this.processing = false;
        this.setLoading(false);
      }

      // Drawer errors must not turn a successful redemption into a failure.
      if (succeeded && this.isConnected && typeof window.SLIDECART_OPEN === 'function') {
        try {
          await window.SLIDECART_OPEN();
        } catch (error) {
          if (DEBUG) {
            console.warn('[McD] Redemption succeeded, but opening the drawer failed', error);
          }
        }
      }
    }

    getGwpManager(timeoutMs = 5000) {
      const getManager = () => {
        const manager = window.WooblesCartGWP;

        return typeof manager?.redeemMcdPromo === 'function' ? manager : null;
      };

      const existingManager = getManager();

      if (existingManager) {
        return Promise.resolve(existingManager);
      }

      if (DEBUG) console.log('[McD] Waiting for cart manager.');

      return new Promise((resolve, reject) => {
        let timeoutId;

        const cleanup = () => {
          document.removeEventListener('woobles:gwp-manager-ready', onReady);
          clearTimeout(timeoutId);
        };

        const onReady = () => {
          const manager = getManager();

          if (!manager) return;

          cleanup();
          resolve(manager);
        };

        document.addEventListener('woobles:gwp-manager-ready', onReady);

        timeoutId = setTimeout(() => {
          cleanup();

          const error = new Error('Redemption manager unavailable');
          error.code = 'manager_unavailable';

          reject(error);
        }, timeoutMs);

        onReady();
      });
    }

    clearMessages() {
      setVisible(this.successMessage, false);
      setVisible(this.errorMessage, false);

      this.inputGroup?.classList.remove('input-group--error');
      this.input?.classList.remove('input--error');
      this.input?.removeAttribute('aria-invalid');
      this.input?.removeAttribute('aria-describedby');
    }

    setLoading(loading) {
      if (this.button) {
        this.button.disabled = loading;
        this.button.setAttribute('aria-busy', String(loading));
      }

      if (this.input) {
        this.input.readOnly = loading;
      }

      this.form?.setAttribute('aria-busy', String(loading));

      setVisible(this.buttonText, !loading);
      setVisible(this.buttonLoader, loading);
    }

    showError(message, fieldError = false) {
      if (DEBUG) {
        console.warn('[McD] Showing error', {
          message,
          fieldError,
        });
      }

      setVisible(this.successMessage, false);

      if (this.errorMessageText) {
        this.errorMessageText.textContent = message;
      }

      setVisible(this.errorMessage, true);

      if (fieldError) {
        this.inputGroup?.classList.add('input-group--error');
        this.input?.classList.add('input--error');
        this.input?.setAttribute('aria-invalid', 'true');

        if (this.errorMessageText?.id) {
          this.input?.setAttribute('aria-describedby', this.errorMessageText.id);
        }
      }
    }
  }

  customElements.define(TAG, USPrizesRedemptionField);

  if (DEBUG) {
    console.log('[McD] Debug redemption component registered:', TAG);
  }
})();
